import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';

import {
  Button,
  FormCheckbox,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@/_components';
import { FormLayout } from '@/_containers';
import { stopPropagate } from '@/_shared';

import {
  paymentMethodFieldTypeOptions,
  paymentMethodFieldTypeToOption,
} from '../constants/payment-method-field-type-options';
import type { PaymentMethodFieldFormRowSchema } from '../schemas/payment-method-field-query-schema';
import { paymentMethodFieldTypeSchema } from '../schemas/payment-method-field-query-schema';
import { normalizeCommaSeparatedOptions } from '../utils/normalize-payment-method-field-options';

const paymentMethodFieldModalFormSchema = z
  .object({
    label: z.string().min(1, 'Label is required'),
    options: z.string(),
    type: z.object({
      name: z.string(),
      value: paymentMethodFieldTypeSchema,
    }),
    is_required: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.type.value === 'select') {
      const normalized = normalizeCommaSeparatedOptions(data.options);
      if (!normalized) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: 'Enter at least one option, separated by commas',
        });
      }
    }
  });

type PaymentMethodFieldModalFormSchema = z.infer<typeof paymentMethodFieldModalFormSchema>;

type AddEditPaymentMethodFieldModalProps = {
  open: boolean;
  onClose: () => void;
  paymentMethodNumericId: number;
  itemToEdit: PaymentMethodFieldFormRowSchema | null;
  onApply: (row: PaymentMethodFieldFormRowSchema) => void;
};

export function AddEditPaymentMethodFieldModal({
  open,
  onClose,
  paymentMethodNumericId,
  itemToEdit,
  onApply,
}: AddEditPaymentMethodFieldModalProps) {
  const isEditMode = !!itemToEdit;
  const form = useForm<PaymentMethodFieldModalFormSchema>({
    resolver: zodResolver(paymentMethodFieldModalFormSchema),
    defaultValues: {
      label: '',
      type: paymentMethodFieldTypeOptions()[0],
      options: '',
      is_required: false,
    },
    shouldFocusError: true,
  });

  const typeValue = useWatch({ control: form.control, name: 'type.value' });

  useEffect(() => {
    if (!open) return;
    if (!itemToEdit) {
      form.reset({
        label: '',
        type: paymentMethodFieldTypeOptions()[0],
        options: '',
        is_required: false,
      });
      return;
    }
    form.reset({
      label: itemToEdit.label,
      type: paymentMethodFieldTypeToOption(itemToEdit.type),
      options: itemToEdit.options,
      is_required: itemToEdit.is_required,
    });
  }, [open, itemToEdit, form]);

  const onSubmit = useCallback(
    (values: PaymentMethodFieldModalFormSchema) => {
      const normalizedOptions = values.type.value === 'select' ? normalizeCommaSeparatedOptions(values.options) : '';

      const row: PaymentMethodFieldFormRowSchema = {
        id: itemToEdit?.id ?? -Date.now(),
        payment_method_id: paymentMethodNumericId,
        type: values.type.value,
        label: values.label.trim(),
        options: normalizedOptions,
        is_required: values.is_required,
      };

      onApply(row);
    },
    [itemToEdit, paymentMethodNumericId, onApply]
  );

  return (
    <Modal open={open} onClose={onClose} disableBackdropClosing>
      <ModalHeader>{isEditMode ? 'Edit Field' : 'Add Field'}</ModalHeader>

      <FormProvider {...form}>
        <form onSubmit={stopPropagate(form.handleSubmit(onSubmit))}>
          <ModalBody>
            <FormLayout>
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormInput {...field} formItemClassName="col-span-12" label="Label (required)" />
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field: { value, onChange } }) => (
                  <FormSelect
                    label="Type"
                    formItemClassName="col-span-12 sm:col-span-6"
                    value={value}
                    onChange={(next) => {
                      if (next == null) return;
                      onChange(next);
                      if (next.value === 'text') {
                        form.setValue('options', '');
                      }
                    }}
                    options={paymentMethodFieldTypeOptions()}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.value}
                  />
                )}
              />
              <div className="col-span-12 pb-1.5 sm:col-span-6">
                <FormField
                  control={form.control}
                  name="is_required"
                  render={({ field }) => (
                    <div className="pb-1.5">
                      <Label className="mb-1.5 -translate-y-2 text-[11px]">Required</Label>
                      <FormCheckbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        label="Yes"
                      />
                    </div>
                  )}
                />
              </div>
              {typeValue === 'select' && (
                <FormField
                  control={form.control}
                  name="options"
                  render={({ field }) => (
                    <FormTextarea
                      {...field}
                      formItemClassName="col-span-12"
                      label="Options (comma-separated)"
                      placeholder="e.g. visa, mastercard, amex"
                      rows={3}
                    />
                  )}
                />
              )}
            </FormLayout>
          </ModalBody>

          <ModalFooter className="mt-6">
            <Button modal as="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button modal type="submit">
              {isEditMode ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
